import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { UserCircle, X } from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import {
  QUALIFICATION_FIELDS,
  QUALIFICATION_SELECT,
  heldQualifications,
  type UserQualificationRow,
} from "@/lib/user-qualifications";

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex justify-between items-start py-2">
      <span className="text-sm text-muted-foreground">{label}</span>
      <span className="text-sm font-medium text-right max-w-[60%]">{value || "—"}</span>
    </div>
  );
}

interface UserQualificationsSheetProps {
  user: { email: string; full_name?: string | null; roleLabels?: string[] } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Right-hand popout listing a user's Workday-synced qualifications. */
export function UserQualificationsSheet({ user, open, onOpenChange }: UserQualificationsSheetProps) {
  const email = user?.email?.toLowerCase() ?? null;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["user-qualifications", email],
    enabled: open && !!email,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_qualifications" as never)
        .select(QUALIFICATION_SELECT)
        .eq("email", email as string)
        .maybeSingle();
      if (error) throw error;
      return (data ?? null) as unknown as UserQualificationRow | null;
    },
  });

  if (!user) return null;

  const held = heldQualifications(data ?? null);

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        className="w-full lg:w-[440px] lg:max-w-[440px] max-w-full overflow-y-auto [&>button.absolute]:hidden p-0"
      >
        <div className="flex items-center justify-between px-4 pt-4 pb-3">
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="px-4 pb-4">
          <div className="flex items-center gap-2">
            <UserCircle className="h-5 w-5 text-muted-foreground" />
            <h2 className="text-lg font-semibold truncate">{user.full_name || user.email}</h2>
          </div>
          <p className="mt-1 text-sm text-muted-foreground truncate">{user.email}</p>
          {user.roleLabels?.length ? (
            <div className="mt-2 flex flex-wrap gap-1">
              {user.roleLabels.map((label) => (
                <Badge key={label} variant="secondary">
                  {label}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>

        <div className="px-4 pb-8 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Qualifications</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              {isLoading ? (
                <p className="py-2 text-sm text-muted-foreground">Loading…</p>
              ) : isError ? (
                <p className="py-2 text-sm text-muted-foreground">
                  Couldn't load qualifications.
                </p>
              ) : !data ? (
                <div className="py-2">
                  <p className="text-sm font-medium">No qualification record</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    This person hasn't appeared in a Workday roster sync yet.
                  </p>
                </div>
              ) : (
                <>
                  <div className="py-2">
                    {held.length === 0 ? (
                      <p className="text-sm text-muted-foreground">None held.</p>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {held.map((code) => (
                          <Badge key={code} variant="secondary">
                            {code}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mt-2 divide-y border-t">
                    {QUALIFICATION_FIELDS.map((f) => (
                      <DetailRow
                        key={f.column}
                        label={f.code}
                        value={data[f.column] === true ? "Yes" : "No"}
                      />
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {data ? (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Sync</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <DetailRow label="Status" value={data.is_active === false ? "Inactive" : "Active"} />
                <DetailRow
                  label="Last synced"
                  value={
                    data.last_synced_at
                      ? format(new Date(data.last_synced_at), "MMM d, yyyy h:mm a")
                      : undefined
                  }
                />
              </CardContent>
            </Card>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
